export function safeParseJson(s) {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

export function parseRequiredImmutablePairs(text, maxPairs = 5) {
  // Parses a JSON array like ["attr=value", ...] into [{key,value}, ...].
  // Returns a variable-length array (no padding). New pools/rules start empty.
  const arr = safeParseJson(text);
  const pairs = [];
  if (Array.isArray(arr)) {
    for (const item of arr) {
      if (typeof item !== "string") continue;
      const s = item;
      const eq = s.indexOf("=");
      if (eq >= 0) pairs.push({ key: s.slice(0, eq), value: s.slice(eq + 1) });
      else pairs.push({ key: s, value: "" });
      if (maxPairs && pairs.length >= maxPairs) break;
    }
  }
  return pairs;
}

export function buildRequiredImmutableTextFromPairs(pairs) {
  const arr = [];
  for (const p of pairs || []) {
    const k = String(p?.key ?? "").trim();
    if (!k) continue;
    const v = String(p?.value ?? "").trim();
    arr.push(`${k}=${v}`);
  }
  return JSON.stringify(arr, null, 2);
}

/** Pool types 1 (Reward), 5 (Repeatable), 6 (Stake Claim) with a real NFT collection (not account-based). */
export function poolSupportsRequiredMutable(poolType, collectionName) {
  const t = Number(poolType || 1);
  if (![1, 5, 6].includes(t)) return false;
  return String(collectionName || "").trim().toLowerCase() !== "account";
}

/** Pool types 1 and 5 support configurable claim scope (npoolscope / setpoolscope). */
export function poolSupportsClaimScope(poolType) {
  const t = Number(poolType || 1);
  return t === 1 || t === 5;
}

export const CLAIM_SCOPE_NFT = 0;
export const CLAIM_SCOPE_ACCOUNT = 1;

export function claimScopeLabel(claimScope) {
  return Number(claimScope) === CLAIM_SCOPE_ACCOUNT ? "account" : "nft";
}

/**
 * Whether setpoolscope must be sent (and nauths permission required).
 * Default per-NFT on a new pool needs no npoolscope row / no setpoolscope action.
 */
export function poolClaimScopeNeedsSetpoolscope(formClaimScope, selectedPool, onChainClaimScope) {
  if (!selectedPool) {
    return Number(formClaimScope ?? CLAIM_SCOPE_NFT) === CLAIM_SCOPE_ACCOUNT;
  }
  const cs = Number(formClaimScope ?? CLAIM_SCOPE_NFT);
  const formNorm = cs === CLAIM_SCOPE_ACCOUNT ? CLAIM_SCOPE_ACCOUNT : CLAIM_SCOPE_NFT;
  if (formNorm === CLAIM_SCOPE_ACCOUNT) return true;
  const onChain = Number(onChainClaimScope ?? CLAIM_SCOPE_NFT);
  return onChain === CLAIM_SCOPE_ACCOUNT;
}

export function extractRequirementKey(entry) {
  const s = String(entry ?? "");
  const eq = s.indexOf("=");
  if (eq < 0) return s.trim();
  return s.slice(0, eq).trim();
}

export function parseRequiredMutablePairs(text, maxPairs = 5) {
  const arr = safeParseJson(text);
  const pairs = [];
  if (Array.isArray(arr)) {
    for (const item of arr) {
      if (typeof item !== "string") continue;
      const s = item;
      const eq = s.indexOf("=");
      if (eq >= 0) pairs.push({ key: s.slice(0, eq), value: s.slice(eq + 1) });
      else pairs.push({ key: s, value: "" });
      if (maxPairs && pairs.length >= maxPairs) break;
    }
  }
  return pairs;
}

export function buildRequiredMutableTextFromPairs(pairs) {
  const arr = [];
  for (const p of pairs || []) {
    const k = String(p?.key ?? "").trim();
    if (!k) continue;
    const v = String(p?.value ?? "").trim();
    arr.push(v ? `${k}=${v}` : k);
  }
  return JSON.stringify(arr, null, 2);
}

/** Serialize UI rows to on-chain requiredMutable string[]. */
export function requiredMutableEntriesFromPairs(pairs) {
  const arr = [];
  for (const p of pairs || []) {
    const k = String(p?.key ?? "").trim();
    if (!k) continue;
    const v = String(p?.value ?? "").trim();
    arr.push(v ? `${k}=${v}` : k);
  }
  return arr;
}

/** Compare on-chain vs form requiredMutable (order-sensitive). */
export function requiredMutableArraysEqual(a, b) {
  const na = (Array.isArray(a) ? a : []).map(String);
  const nb = (Array.isArray(b) ? b : []).map(String);
  if (na.length !== nb.length) return false;
  for (let i = 0; i < na.length; i++) {
    if (na[i] !== nb[i]) return false;
  }
  return true;
}

/**
 * Client-side validation aligned with contract assertRequiredMutableLimits.
 * @returns {{ errors: string[], warnings: string[] }}
 */
export function validateRequiredMutablePairs(pairs) {
  const errors = [];
  const warnings = [];
  const list = Array.isArray(pairs) ? pairs : [];
  const entries = requiredMutableEntriesFromPairs(list);

  if (entries.length > 5) errors.push("Required Mutable: max 5 entries.");
  const keys = new Set();
  for (let i = 0; i < list.length; i++) {
    const k = String(list[i]?.key ?? "").trim();
    const v = String(list[i]?.value ?? "").trim();
    if (!k && !v) {
      errors.push(`Required Mutable row ${i + 1}: both fields are empty. Either fill them or delete the row.`);
      continue;
    }
    if (!k) {
      errors.push(`Required Mutable row ${i + 1}: Attribute name is required.`);
      continue;
    }
    const entry = v ? `${k}=${v}` : k;
    if (entry.length > 200) errors.push(`Required Mutable row ${i + 1}: entry too long (max 200 characters).`);
    if (entry.includes("=") && entry.indexOf("=") !== entry.lastIndexOf("=")) {
      errors.push(`Required Mutable row ${i + 1}: must contain at most one '='.`);
    }
    if (v === "" && entry.endsWith("=")) warnings.push(`Required Mutable row ${i + 1}: empty value after '=' is ignored on-chain.`);
    const key = extractRequirementKey(entry);
    if (!key) errors.push(`Required Mutable row ${i + 1}: attribute name must not be empty.`);
    else if (keys.has(key)) errors.push(`Required Mutable: duplicate key '${key}'.`);
    else keys.add(key);
  }
  return { errors, warnings };
}

export async function postJson(url, body) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
  const txt = await res.text();
  try {
    return JSON.parse(txt);
  } catch {
    return null;
  }
}

export function serializeError(e) {
  if (!e) return { message: "Unknown error" };
  return {
    name: e.name,
    message: e.message ?? String(e),
    stack: e.stack,
  };
}
